import { useEffect, useRef, useState, type ReactNode } from "react";
import { BrandedPageLoader, type BrandedPageLoaderProps } from "@/components/public/BrandedPageLoader";
import { DEFAULT_LOADER_MIN_MS } from "@/lib/lazyWithMinimumDelay";

type MinimumDurationLoaderProps = {
  ready: boolean;
  minMs?: number;
  loaderProps?: BrandedPageLoaderProps;
  children: ReactNode;
};

/**
 * Keeps BrandedPageLoader visible for at least `minMs` after mount, even if `ready` resolves quickly.
 */
export function MinimumDurationLoader({
  ready,
  minMs = DEFAULT_LOADER_MIN_MS,
  loaderProps,
  children,
}: MinimumDurationLoaderProps) {
  const [showLoader, setShowLoader] = useState(true);
  const mountedAtRef = useRef(Date.now());

  useEffect(() => {
    if (!ready) {
      mountedAtRef.current = Date.now();
      setShowLoader(true);
      return;
    }

    const elapsed = Date.now() - mountedAtRef.current;
    const remaining = Math.max(0, minMs - elapsed);
    const timer = window.setTimeout(() => setShowLoader(false), remaining);
    return () => window.clearTimeout(timer);
  }, [ready, minMs]);

  if (!ready || showLoader) {
    return <BrandedPageLoader {...loaderProps} />;
  }

  return <>{children}</>;
}
